import { StateCreator, create } from "zustand";
import { persist } from "zustand/middleware";
import localforage from "localforage";
import { formatPrice } from "../components/paymentUtil";
import { Offerid } from "../services/stripe";
import {
  Gr4vyCheckoutData,
  CheckoutSessionResponse,
} from "../typings/paymentsServiceTypes";
import { AffirmTransactionStatus, MSO } from "../typings/enums";
import { ProductNames } from "../utils/surveyUtils";

interface PatientGuidData {
  consultGuid: string;
  episodeGuid: string;
}

interface PersistedStore {
  offerid: Offerid | undefined;
  setOfferid: (offerid: Offerid | undefined) => void;
  affirmTransactionStatus: AffirmTransactionStatus | undefined;
  setAffirmTransactionStatus: (status: AffirmTransactionStatus) => void;
  isApptSelected: boolean;
  setIsApptSelected: (isApptSelected: boolean) => void;
  patientGuidData: PatientGuidData | null;
  setPatientGuidData: (patientGuidData: PatientGuidData) => void;
  programPrice: number;
  setProgramPrice: (programPrice: number) => void;
  selectedMembership: ProductNames;
  setSelectedMembership: (membership: ProductNames) => void;
  clientSecret: string;
  setClientSecret: (clientSecret: string) => void;
  token: string;
  setToken: (token: string) => void;
  checkoutData: Gr4vyCheckoutData;
  setCheckoutData: (checkoutData: Gr4vyCheckoutData) => void;
  setPreferredMedicationName: (medicationName: string) => void;
  preferredMedicationName: string;
  hasPaid: boolean;
  setHasPaid: (hasPaid: boolean) => void;
  hasCreatedAccount: boolean;
  setHasCreatedAccount: (hasCreatedAccount: boolean) => void;
  mso: MSO | undefined;
  setMso: (mso: MSO | undefined) => void;
  isMicrodosingUser: boolean | undefined;
  setIsMicrodosingUser: (isMicrodosingUser: boolean) => void;
}

const defaultCheckoutData = {
  token: "",
  buyerID: "",
  amount: 0,
  cartItems: [],
  currency: "",
  country: "",
  locale: "",
  shippingDetailsId: "",
  totalTax: 0,
  checkoutSession: {} as CheckoutSessionResponse,
} as Gr4vyCheckoutData;

const storage = {
  getItem: async (name: string) => {
    const value = await localforage.getItem(name);
    return value ? JSON.parse(value as string) : null;
  },
  setItem: async (name: string, value: unknown) => {
    await localforage.setItem(name, JSON.stringify(value));
  },
  removeItem: async (name: string) => {
    await localforage.removeItem(name);
  },
};

export const usePersistStore = create<PersistedStore>(
  persist(
    (set) => ({
      offerid: undefined,
      setOfferid: (offerid) => set({ offerid }),
      affirmTransactionStatus: undefined,
      setAffirmTransactionStatus: (status) =>
        set({ affirmTransactionStatus: status }),
      isApptSelected: false,
      setIsApptSelected: (isApptSelected) => set({ isApptSelected }),
      patientGuidData: null,
      setPatientGuidData: (patientGuidData) => set({ patientGuidData }),
      programPrice: 0,
      setProgramPrice: (programPrice) => set({ programPrice }),
      selectedMembership: "" as ProductNames,
      setSelectedMembership: (selectedMembership: ProductNames) =>
        set({ selectedMembership }),
      clientSecret: "",
      setClientSecret: (clientSecret: string) => set({ clientSecret }),
      token: "",
      setToken: (token: string) => set({ token }),
      checkoutData: defaultCheckoutData,
      setCheckoutData: (checkoutData: Gr4vyCheckoutData) =>
        set({ checkoutData }),
      setPreferredMedicationName: (preferredMedicationName) =>
        set({ preferredMedicationName }),
      preferredMedicationName: "",
      hasPaid: false,
      setHasPaid: (hasPaid: boolean) => set({ hasPaid }),
      hasCreatedAccount: false,
      setHasCreatedAccount: (hasCreatedAccount: boolean) =>
        set({ hasCreatedAccount }),
      mso: undefined,
      setMso: (mso: MSO | undefined) => set({ mso }),
      isMicrodosingUser: undefined,
      setIsMicrodosingUser: (isMicrodosingUser: boolean) =>
        set({ isMicrodosingUser }),
    }),
    {
      name: "funnel-quiz-state",
      storage, // Use localforage for storage
    }
  ) as StateCreator<PersistedStore>
);

export const clearPersistedStore = async () => {
  try {
    // Reset in-memory state synchronously so callers can immediately set new
    // values without racing against the async localforage cleanup below.
    usePersistStore.setState({
      offerid: undefined,
      affirmTransactionStatus: undefined,
      isApptSelected: false,
      programPrice: 0,
      selectedMembership: "" as ProductNames,
      checkoutData: defaultCheckoutData,
      patientGuidData: null,
      clientSecret: "",
      preferredMedicationName: "",
      token: "",
      hasPaid: false,
      hasCreatedAccount: false,
      mso: undefined,
      isMicrodosingUser: undefined,
    });
    await localforage.removeItem("funnel-quiz-state");
  } catch (error) {
    console.error("Failed to clear persisted store:", error);
  }
};

export const setPersistedProgramPrice = (price: number) => {
  usePersistStore.getState().setProgramPrice(price);
};

export const getPersistedProgramPrice = () => {
  return usePersistStore.getState().programPrice;
};

export const getFormattedPersistedProgramPrice = (
  currency: string = "USD",
  locale: string = "en-US"
) => {
  const programPrice = getPersistedProgramPrice();
  return formatPrice(programPrice, true, currency, locale);
};
